import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  IconButton,
  InputBase,
  Slider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo } from "react";

import { getLanguageOptions as getAllLanguageOptions } from "Forms/Utils/EnumOptions";
import {
  ActorStatus,
  ActorVoiceStatus,
  AudioBitrate,
  AudioChannels,
  AudioFormat,
  AudioSampleRate,
  type LanguageCodes,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import {
  getTranslation,
  Resources,
  useResource,
} from "Infrastructure/Translations/Resources";
import { useProjectActorVoicesQuery } from "Projects/Api/Queries/useProjectActorVoicesQuery";
import {
  isVoiceSegment,
  useScriptEditor,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { flattenSpans, reconciledSpans } from "Projects/Utils/spanUtils";
import { BlDropdown } from "Shared/Inputs/BlDropdown";
import type { BlOption } from "Shared/Inputs/BlOption";

interface GeneralSettingsTabContentProps {
  projectID: string;
}

const SettingRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactNode => {
  const theme = useTheme();
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "12px",
          color: theme.palette.text.secondary,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
};

const getLanguageOptions = (): BlOption[] => [
  {
    value: "",
    label: getTranslation(Resources.Projects.DefaultLanguageOption),
  },
  ...getAllLanguageOptions(),
];

const AUDIO_FORMAT_OPTIONS: BlOption[] = [
  { value: AudioFormat.Mp3, label: ".mp3" },
  { value: AudioFormat.Wav, label: ".wav" },
];

const AUDIO_SAMPLE_RATE_OPTIONS: BlOption[] = [
  { value: AudioSampleRate.Hz22050, label: "22 050 Hz" },
  { value: AudioSampleRate.Hz44100, label: "44 100 Hz" },
  { value: AudioSampleRate.Hz48000, label: "48 000 Hz" },
];

const AUDIO_BITRATE_OPTIONS: BlOption[] = [
  { value: AudioBitrate.Kbps64, label: "64 Kbps" },
  { value: AudioBitrate.Kbps128, label: "128 Kbps" },
  { value: AudioBitrate.Kbps192, label: "192 Kbps" },
  { value: AudioBitrate.Kbps256, label: "256 Kbps" },
  { value: AudioBitrate.Kbps320, label: "320 Kbps" },
];

const AUDIO_CHANNELS_OPTIONS: BlOption[] = [
  { value: AudioChannels.Mono, label: "Mono" },
  { value: AudioChannels.Stereo, label: "Stereo" },
];

export const GeneralSettingsTabContent = ({
  projectID,
}: GeneralSettingsTabContentProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const { data: actorVoices } = useProjectActorVoicesQuery(projectID);
  const {
    uiDefaults,
    setUiDefaults,
    audioOutputOptions,
    setAudioOutputOptions,
    script,
    setScript,
    isSingleVoiceSpot,
    applyVoiceToAllVoiceSegments,
    isEditable,
  } = useScriptEditor();

  // Auto-select the default voice when none is set yet. For single-voice spots
  // with a voice already assigned on segments (e.g. locked to a voice used in
  // a previous finished job), surface that voice in the dropdown even if it
  // is no longer in Active status.
  useEffect(() => {
    if (
      !actorVoices ||
      actorVoices.length === 0 ||
      uiDefaults.defaultActorVoiceID
    ) {
      return;
    }

    const segmentVoiceID = script.segments
      .filter(isVoiceSegment)
      .find((s) => s.actorVoiceID)?.actorVoiceID;
    if (
      segmentVoiceID &&
      actorVoices.some((av) => av.actorVoiceID === segmentVoiceID)
    ) {
      setUiDefaults({ defaultActorVoiceID: segmentVoiceID });
      return;
    }

    const firstActive = actorVoices.find(
      (av) =>
        av.actorStatus === ActorStatus.Active &&
        av.voiceStatus === ActorVoiceStatus.Active,
    );
    if (firstActive) {
      if (isSingleVoiceSpot) {
        applyVoiceToAllVoiceSegments(firstActive.actorVoiceID);
      } else {
        setUiDefaults({ defaultActorVoiceID: firstActive.actorVoiceID });
      }
    }
  }, [
    actorVoices,
    uiDefaults.defaultActorVoiceID,
    setUiDefaults,
    isSingleVoiceSpot,
    applyVoiceToAllVoiceSegments,
    script.segments,
  ]);

  const actorVoiceOptions: BlOption[] = useMemo(() => {
    const options: BlOption[] =
      actorVoices?.map((av) => ({
        value: av.actorVoiceID,
        label: `${av.actorName} – ${av.voiceName}`,
        disabled:
          av.actorStatus === ActorStatus.Inactive ||
          av.voiceStatus !== ActorVoiceStatus.Active,
      })) ?? [];
    return options;
  }, [actorVoices]);

  const handleAddVariable = () => {
    setScript({
      ...script,
      variables: [...script.variables, { name: "", defaultValue: "" }],
    });
  };

  const handleVariableNameChange = (index: number, name: string) => {
    const variables = script.variables.map((v, i) =>
      i === index ? { ...v, name } : v,
    );
    setScript({ ...script, variables });
  };

  const handleVariableValueChange = (index: number, defaultValue: string) => {
    const variables = script.variables.map((v, i) =>
      i === index ? { ...v, defaultValue } : v,
    );
    setScript({ ...script, variables });
  };

  const handleDeleteVariable = (index: number) => {
    const removed = script.variables[index];
    const variables = script.variables.filter((_, i) => i !== index);

    if (!removed?.name) {
      setScript({ ...script, variables });
      return;
    }

    const escaped = removed.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const varPattern = new RegExp(`\\{\\{\\s*${escaped}\\s*\\}\\}`, "g");

    const segments = script.segments.map((seg) => {
      if (!isVoiceSegment(seg)) return seg;
      const flat = flattenSpans(seg.spans);
      const stripped = flat.replace(varPattern, "");
      if (stripped === flat) return seg;
      return { ...seg, spans: reconciledSpans(seg.spans, stripped) };
    });

    setScript({ ...script, variables, segments });
  };

  return (
    <Stack spacing={Spacing.M}>
      {/* Default voice model */}
      <SettingRow label={t(Resources.JobDetail.DefaultVoiceModel)}>
        <BlDropdown
          name="defaultVoice"
          options={actorVoiceOptions}
          value={uiDefaults.defaultActorVoiceID ?? ""}
          disabled={!isEditable}
          onChange={(v) => {
            const voiceID = (v as string) || null;
            if (isSingleVoiceSpot && voiceID) {
              applyVoiceToAllVoiceSegments(voiceID);
            } else {
              setUiDefaults({ defaultActorVoiceID: voiceID });
            }
          }}
        />
      </SettingRow>

      {/* Global language */}
      <SettingRow label={t(Resources.JobDetail.GlobalLanguage)}>
        <BlDropdown
          name="globalLanguage"
          options={getLanguageOptions()}
          value={uiDefaults.defaultLanguage ?? ""}
          showEmptyOptionLabel
          disabled={!isEditable}
          onChange={(v) =>
            setUiDefaults({
              defaultLanguage: ((v as string) || null) as LanguageCodes | null,
            })
          }
        />
      </SettingRow>

      {/* Global speed */}
      <SettingRow
        label={`${t(Resources.JobDetail.GlobalSpeed)}: ×${uiDefaults.defaultSpeedFactor.toFixed(2)}`}
      >
        <Slider
          value={uiDefaults.defaultSpeedFactor}
          min={0.5}
          max={2.0}
          step={0.05}
          disabled={!isEditable}
          onChange={(_, v) =>
            setUiDefaults({ defaultSpeedFactor: v as number })
          }
          sx={{
            color: "primary.main",
            "& .MuiSlider-thumb": { width: 14, height: 14 },
          }}
        />
      </SettingRow>

      {/* Global volume */}
      <SettingRow
        label={`${t(Resources.JobDetail.GlobalVolume)}: ${Math.round(uiDefaults.defaultVolumePercent)}%`}
      >
        <Slider
          value={uiDefaults.defaultVolumePercent}
          min={0}
          max={100}
          step={1}
          disabled={!isEditable}
          onChange={(_, v) =>
            setUiDefaults({ defaultVolumePercent: v as number })
          }
          sx={{
            color: "primary.main",
            "& .MuiSlider-thumb": { width: 14, height: 14 },
          }}
        />
      </SettingRow>

      {/* Audio format */}
      <SettingRow label={t(Resources.JobDetail.AudioFormat)}>
        <BlDropdown
          name="audioFormat"
          options={AUDIO_FORMAT_OPTIONS}
          value={audioOutputOptions.format}
          disabled={!isEditable}
          onChange={(v) =>
            setAudioOutputOptions({
              ...audioOutputOptions,
              format: v as AudioFormat,
            })
          }
        />
      </SettingRow>

      {/* Sample rate */}
      <SettingRow label={t(Resources.JobDetail.SampleRate)}>
        <BlDropdown
          name="sampleRate"
          options={AUDIO_SAMPLE_RATE_OPTIONS}
          value={audioOutputOptions.sampleRate}
          disabled={!isEditable}
          onChange={(v) =>
            setAudioOutputOptions({
              ...audioOutputOptions,
              sampleRate: v as AudioSampleRate,
            })
          }
        />
      </SettingRow>

      {/* Bitrate (MP3 only) */}
      {audioOutputOptions.format === AudioFormat.Mp3 && (
        <SettingRow label={t(Resources.JobDetail.Bitrate)}>
          <BlDropdown
            name="bitrate"
            options={AUDIO_BITRATE_OPTIONS}
            value={audioOutputOptions.bitrate}
            disabled={!isEditable}
            onChange={(v) =>
              setAudioOutputOptions({
                ...audioOutputOptions,
                bitrate: v as AudioBitrate,
              })
            }
          />
        </SettingRow>
      )}

      {/* Channels */}
      <SettingRow label={t(Resources.JobDetail.AudioChannels)}>
        <BlDropdown
          name="audioChannels"
          options={AUDIO_CHANNELS_OPTIONS}
          value={audioOutputOptions.channels}
          disabled={!isEditable}
          onChange={(v) =>
            setAudioOutputOptions({
              ...audioOutputOptions,
              channels: v as AudioChannels,
            })
          }
        />
      </SettingRow>

      {/* Variables */}
      <SettingRow label={t(Resources.JobDetail.Variables)}>
        <Stack spacing={Spacing.XS}>
          {script.variables.map((variable, index) => (
            <Box
              // biome-ignore lint/suspicious/noArrayIndexKey: stable by index in variable list
              key={index}
              sx={{ display: "flex", gap: 1, alignItems: "center" }}
            >
              <InputBase
                value={variable.name}
                placeholder="název"
                disabled={!isEditable}
                onChange={(e) =>
                  handleVariableNameChange(index, e.target.value)
                }
                sx={{
                  flex: 1,
                  backgroundColor: theme.colors.backgroundInput,
                  border: `1px solid ${
                    !variable.name
                      ? theme.palette.error.main
                      : theme.colors.border
                  }`,
                  borderRadius: theme.spacing(1),
                  px: 1.5,
                  fontSize: "12px",
                  color: theme.palette.text.primary,
                  fontFamily: "monospace",
                }}
              />
              <InputBase
                value={variable.defaultValue}
                placeholder="výchozí hodnota"
                disabled={!isEditable}
                onChange={(e) =>
                  handleVariableValueChange(index, e.target.value)
                }
                sx={{
                  flex: 1.5,
                  backgroundColor: theme.colors.backgroundInput,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.spacing(1),
                  px: 1.5,
                  fontSize: "12px",
                  color: theme.palette.text.primary,
                }}
              />
              {isEditable && (
                <IconButton
                  size="small"
                  onClick={() => handleDeleteVariable(index)}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { color: "error.main" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          ))}
          {isEditable && (
            <Button
              variant="text"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddVariable}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                fontSize: "13px",
                p: 0,
              }}
            >
              {t(Resources.JobDetail.AddVariable)}
            </Button>
          )}
        </Stack>
      </SettingRow>
    </Stack>
  );
};
